import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Block,
  CheckCircle,
  Cancel,
  DeleteOutline,
  EditOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import { getRestaurantes, deleteRestaurante, updateRestaurante } from '../services/api';

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const SECTION_ICON_BOX_SX = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  color: '#6366f1',
  flexShrink: 0,
  border: '1px solid rgba(99,102,241,0.18)',
};

const AVATAR_PALETTE = ['#6366f1', '#0891b2', '#059669', '#d97706', '#7c3aed', '#db2777', '#0f766e'];
const getAvatarColor = (name = '') =>
  AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];

const badgeChipSx = {
  height: 26,
  fontWeight: 700,
  fontSize: '0.72rem',
  borderRadius: '999px',
  border: '1px solid transparent',
  '& .MuiChip-label': { px: 1.1 },
};

const StatusChip = ({ active }) => (
  <Chip
    label={active ? 'Sim' : 'Não'}
    size="small"
    icon={active
      ? <CheckCircle sx={{ fontSize: '14px !important', ml: '6px !important' }} />
      : <Cancel sx={{ fontSize: '14px !important', ml: '6px !important' }} />}
    sx={{
      ...badgeChipSx,
      bgcolor: active ? '#f0fdf4' : '#fff1f2',
      color: active ? '#166534' : '#9f1239',
      borderColor: active ? '#bbf7d0' : '#fecdd3',
    }}
  />
);

const RestaurantTable = ({ title, items, onEdit, onDeactivate, onDelete, disablingId, deletingId }) => {
  const headCells = [
    { id: 'id',              label: 'ID',              align: 'left',  width: '6%'  },
    { id: 'nome',            label: 'Nome',            align: 'left'                },
    { id: 'cdm_ativo',       label: 'CDM Ativo',       align: 'left',  width: '15%' },
    { id: 'atendabot_ativo', label: 'Atendabot Ativo', align: 'left',  width: '16%' },
    { id: 'acoes',           label: 'Ações',           align: 'right', width: '12%' },
  ];

  return (
    <Box sx={CARD_SX}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 1.5, borderBottom: '1px solid rgba(15,23,42,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
        <Chip
          label={`${items.length} ${items.length === 1 ? 'estabelecimento' : 'estabelecimentos'}`}
          size="small"
          sx={{ ...badgeChipSx, bgcolor: '#f1f5f9', color: '#475467', borderColor: '#e2e8f0' }}
        />
      </Box>

      <TableContainer sx={{ px: { xs: 1, md: 1.5 }, pt: 1.25, pb: 0 }}>
        <Table sx={{ minWidth: 860, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {headCells.map((hc) => (
                <TableCell key={hc.id} align={hc.align} sx={{ width: hc.width, borderBottom: '1px solid #eaecf0', py: 1.75, px: 2.25, fontSize: '0.775rem', fontWeight: 700, color: '#475467', letterSpacing: '0.01em' }}>
                  {hc.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center" sx={{ py: 8, border: 0 }}>
                  <Typography sx={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.9rem' }}>
                    Nenhum item cadastrado nesta categoria.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {items.map((it) => {
              const avatarColor = getAvatarColor(it.nome);
              const initials = (it.nome || '?').slice(0, 2).toUpperCase();
              const cdmActive = !!it.is_ativo && (!!it.instance_id || !!it.clube_global);
              const busy = disablingId === it.id || deletingId === it.id;

              return (
                <TableRow
                  key={it.id}
                  sx={{
                    '& td': { py: 1.75, px: 2.25, borderBottom: '1px solid rgba(15,23,42,0.05)', verticalAlign: 'middle', bgcolor: '#fff', transition: 'background-color 140ms ease' },
                    '& td:first-of-type': { borderLeft: '3px solid transparent', transition: 'border-left-color 140ms ease' },
                    '&:hover td': { bgcolor: '#fafbff' },
                    '&:hover td:first-of-type': { borderLeftColor: '#c7d2fe' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.8rem' }}>#{it.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.67rem', fontWeight: 800, flexShrink: 0, letterSpacing: '0.03em' }}>
                        {initials}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#101828', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it.nome}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell><StatusChip active={cdmActive} /></TableCell>
                  <TableCell><StatusChip active={!!it.atendabot_active} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={it.is_ativo ? 'Inativar' : 'Ativar'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDeactivate(it)}
                            disabled={busy}
                            aria-label={it.is_ativo ? `Inativar restaurante ${it.nome}` : `Ativar restaurante ${it.nome}`}
                            sx={{
                              width: 34, height: 34, borderRadius: '10px', border: '1px solid',
                              borderColor: it.is_ativo ? '#fed7aa' : '#bbf7d0',
                              bgcolor: it.is_ativo ? '#fff7ed' : '#f0fdf4',
                              color: it.is_ativo ? '#c2410c' : '#166534',
                              '&:hover': { bgcolor: it.is_ativo ? '#ffedd5' : '#dcfce7' },
                              '&.Mui-disabled': { opacity: 0.45 },
                              transition: 'all 140ms ease',
                            }}
                          >
                            {it.is_ativo ? <Block sx={{ fontSize: 16 }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(it)}
                          aria-label={`Editar restaurante ${it.nome}`}
                          sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid #c7d2fe', bgcolor: '#eef2ff', color: '#4338ca', '&:hover': { bgcolor: '#e0e7ff' }, transition: 'all 140ms ease' }}
                        >
                          <EditOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(it)}
                            disabled={deletingId === it.id}
                            aria-label={`Excluir restaurante ${it.nome}`}
                            sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', '&:hover': { bgcolor: '#ffe4e6' }, '&.Mui-disabled': { opacity: 0.45 }, transition: 'all 140ms ease' }}
                          >
                            <DeleteOutline sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RestaurantsPage = () => {
  const [items, setItems] = useState([]);
  const [disablingId, setDisablingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await getRestaurantes();
      setItems(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (it) => navigate(`/manager/restaurantes/${it.id}`, { state: it });

  const handleDeactivate = async (it) => {
    const actionLabel = it.is_ativo ? 'Inativar' : 'Ativar';
    if (!window.confirm(`${actionLabel} o restaurante "${it.nome}"?`)) return;
    setDisablingId(it.id);
    try {
      await updateRestaurante(it.id, { is_ativo: !it.is_ativo });
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setDisablingId(null);
    }
  };

  const handleOpenDelete = (it) => setDeleteTarget(it);
  const handleCloseDelete = () => { if (deletingId) return; setDeleteTarget(null); };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await deleteRestaurante(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const foodItems    = useMemo(() => items.filter((it) => it.is_food),  [items]);
  const genericItems = useMemo(() => items.filter((it) => !it.is_food), [items]);
  const activeCount  = useMemo(() => items.filter((it) => it.is_ativo).length, [items]);

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>

          {/* ── Header card ── */}
          <Box sx={CARD_SX}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={2} mb={2.5}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box sx={SECTION_ICON_BOX_SX}>
                    <StorefrontOutlined sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: { xs: '1.25rem', md: '1.4rem' }, letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                      Estabelecimentos
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Gerencie, edite ou exclua os estabelecimentos cadastrados.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/manager/restaurantes/new')}
                  sx={{
                    borderRadius: '12px', px: 2.5, py: 1, fontWeight: 700, boxShadow: 'none', letterSpacing: '0.01em', flexShrink: 0,
                    bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                    transition: 'all 150ms ease',
                  }}
                >
                  Novo
                </Button>
              </Stack>

              <Divider sx={{ mb: 2.5, borderColor: 'rgba(15,23,42,0.06)' }} />

              {/* Stats */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="center"
                divider={<Box sx={{ width: { xs: '60px', md: 1 }, height: { xs: 1, md: 40 }, bgcolor: 'rgba(15,23,42,0.06)' }} />}
                spacing={{ xs: 2, md: 0 }}
              >
                {[
                  { value: items.length,               label: 'Estabelecimentos', color: '#0f172a' },
                  { value: activeCount,                 label: 'Ativos',           color: '#166534', px: { md: 5 } },
                  { value: items.length - activeCount,  label: 'Inativos',         color: '#9f1239', px: { md: 5 } },
                ].map(({ value, label, color, px }) => (
                  <Box key={label} sx={{ flex: 1, textAlign: 'center', px: px || { md: 3 } }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 800, color, lineHeight: 1.15, mb: 0.25 }}>
                      {value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8' }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* ── Tables ── */}
          <RestaurantTable title="Restaurantes" items={foodItems} onEdit={handleEdit} onDeactivate={handleDeactivate} onDelete={handleOpenDelete} disablingId={disablingId} deletingId={deletingId} />
          <RestaurantTable title="Genérico"     items={genericItems} onEdit={handleEdit} onDeactivate={handleDeactivate} onDelete={handleOpenDelete} disablingId={disablingId} deletingId={deletingId} />
        </Stack>
      </Box>

      {/* ── Delete dialog ── */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        BackdropProps={{ sx: { backgroundColor: 'rgba(15,23,42,0.5)' } }}
        PaperProps={{ sx: { borderRadius: '20px', boxShadow: '0 28px 56px rgba(15,23,42,0.18)', border: '1px solid rgba(15,23,42,0.06)' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', pb: 1, pt: 2.5, px: 3 }}>
          Excluir estabelecimento?
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
            Esta ação é irreversível e todos os dados cadastrados serão excluídos.
          </Typography>
          {deleteTarget?.nome && (
            <Box sx={{ mt: 2, px: 1.5, py: 1.25, borderRadius: '12px', bgcolor: '#fff1f2', border: '1px solid #fecdd3', display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: getAvatarColor(deleteTarget.nome), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.65rem', fontWeight: 800, flexShrink: 0 }}>
                {deleteTarget.nome.slice(0, 2).toUpperCase()}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#9f1239' }}>
                {deleteTarget.nome}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDelete} disabled={Boolean(deletingId)} sx={{ borderRadius: '10px', fontWeight: 600, color: '#475467', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f8fafc' } }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={Boolean(deletingId)}
            sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: '#be123c', boxShadow: 'none', '&:hover': { bgcolor: '#9f1239', boxShadow: '0 4px 12px rgba(190,18,60,0.3)' }, '&.Mui-disabled': { opacity: 0.55 } }}
          >
            {deletingId ? 'Excluindo...' : 'Excluir definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestaurantsPage;
